import { useEffect, useState, type ReactNode } from 'react';
import type { ConnectionStatus } from '../lib/useSession.js';
import { Button } from './ui/Button.js';
import styles from './RoomHeader.module.css';

export interface RoomHeaderYou {
  readonly name: string;
  readonly isHost: boolean;
  readonly role: 'player' | 'observer';
}

export interface RoomHeaderProps {
  /** The wordmark on the left — what kind of room this is, not the room's own title. */
  readonly brand: string;
  /** A room's own name, e.g. a retro's title. Planning rooms have none. */
  readonly title?: string;
  /** The URL segment this room type joins under: "room" or "retro". */
  readonly joinPath: 'room' | 'retro';
  readonly roomId: string;
  readonly you: RoomHeaderYou | null;
  readonly status: ConnectionStatus;
  /** Who's here, for a surface with no other seat visualization — the retro board's avatars. */
  readonly presence?: ReactNode;
  onLeave: () => void;
}

const STATUS_TEXT: Record<ConnectionStatus, string> = {
  connecting: 'Connecting',
  live: 'Live',
  reconnecting: 'Reconnecting',
  rejected: 'Disconnected',
};

export function RoomHeader({ brand, title, joinPath, roomId, you, status, presence, onLeave }: RoomHeaderProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1_800);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copyLink() {
    const link = `${window.location.origin}/${joinPath}/${roomId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
    } catch {
      // Clipboard is blocked without a user gesture in some browsers; the code
      // is on screen anyway, so this needs no error state.
    }
  }

  const statusClass = [styles.status, status === 'live' ? styles.live : undefined, status === 'reconnecting' || status === 'connecting' ? styles.away : undefined]
    .filter(Boolean)
    .join(' ');

  return (
    <header className={styles.header}>
      <div className={styles.inner}>
        <span className={styles.brand}>
          <span className={styles.brandDot} aria-hidden="true" />
          {brand}
        </span>

        {title && (
          <>
            <span className={styles.divider} aria-hidden="true" />
            <span className={styles.title}>{title}</span>
          </>
        )}

        <span className={styles.divider} aria-hidden="true" />

        <span className={styles.code}>
          {roomId}
          <button type="button" className={`${styles.copy} ${copied ? styles.copied : ''}`} onClick={copyLink}>
            {copied ? 'Copied' : 'Copy link'}
          </button>
        </span>

        <span className={statusClass} role="status">
          <span className={styles.dot} aria-hidden="true" />
          {STATUS_TEXT[status]}
        </span>

        <span className={styles.spacer} />

        {presence}

        {you && (
          <span className={styles.you}>
            <span className={styles.youName}>{you.name}</span>
            {you.isHost ? '· host' : you.role === 'observer' ? '· observer' : ''}
          </span>
        )}

        <Button variant="danger" size="small" onClick={onLeave}>
          Leave
        </Button>
      </div>
    </header>
  );
}
